import React, { useCallback, useEffect, useState } from 'react';
import AppModal from '@/components/AppModal/AppModal';
import styles from './SettingsModal.module.scss';
import AppIcon from '@/components/school-management/AppIcon/AppIcon';
import { useAppDispatch, useAppSelector } from '@/store';
import userThunks from '@/store/thunks/user';
import { userService } from '@/services';
import { useNavigate } from 'react-router-dom';
import { useAdminTranslation } from '@/admin/hooks/useAdminTranslation';
import { authSelector } from '@/store/reducers/auth';
import UsePasswordChange from '@/admin/hooks/UsePasswordChange';
import Input from '@/components/school-management/Input/Input';
import Button from '@/components/school-management/Button/Button';
import { icons } from '@/school-management/utils/icons';
export type SettingsModalProps = {
  show: boolean;
  onClose: () => void;
};

type SettingsView =
  | 'root'
  | 'language'
  | 'interface-language'
  | 'about-me'
  | 'security'
  | 'change-password';

const SettingsModal: React.FC<SettingsModalProps> = ({ show, onClose }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useAdminTranslation();
  const { user } = useAppSelector(authSelector);

  const fromToLanguageParam = user?.metadata?.languagePreferences?.fromToLanguageParams?.[0] || '';
  const isSingleFromToLanguageParam = (() => {
    if (!fromToLanguageParam) return false;
    const [from, to] = fromToLanguageParam.split('-').map(v => v.trim().toLowerCase());
    return Boolean(from) && Boolean(to) && from === to;
  })();
  const canOpenInterfaceLanguage = !isSingleFromToLanguageParam;

  const languageMap: Record<string, keyof typeof icons> = {
    en: 'en',
    he: 'he',
    pt: 'pt',
    es: 'es',
  };
  const nativeLanguageIconKey =
    languageMap[user?.metadata.languagePreferences.nativeLanguage as string];
  const learningLanguageIconKey =
    languageMap[user?.metadata.languagePreferences.learningLanguage as string];
  const interfaceLanguageIconKey =
    languageMap[user?.metadata.languagePreferences.interfaceLanguage as string];

  const [view, setView] = useState<SettingsView>('root');
  const [selectedInterfaceLang, setSelectedInterfaceLang] = useState<string>(
    (user?.metadata?.languagePreferences?.interfaceLanguage as string) || 'en'
  );
  const passwordChange = UsePasswordChange();
  const { resetFields } = passwordChange;
  const npField = passwordChange.changePasswordFields.find((f: any) => f.id === 'new_password');
  const newPwdVal = npField?.value ?? '';
  const requirementsRegex =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\^$*.{}\[\]()?\-\"!@#%&\/\\,><':;|_~`+=])[A-Za-z\d\^$*.{}\[\]()?\-\"!@#%&\/\\,><':;|_~`+=]{8,}$/;
  const showRequirementsError =
    Boolean(npField?.error) && !!newPwdVal && !requirementsRegex.test(newPwdVal);
  const passwordRequirementsList = (
    <>
      <div className={styles.requirement}>
        <AppIcon icon='questionMark' width={20} height={20} />
        <span>{t('settings.passwordRequirements.passwordMinimumLength')}</span>
      </div>
      <div className={styles.requirement}>
        <AppIcon icon='questionMark' width={20} height={20} />
        <span>{t('settings.passwordRequirements.atLeastOneNumber')}</span>
      </div>
      <div className={styles.requirement}>
        <AppIcon icon='questionMark' width={20} height={20} />
        <span>{t('settings.passwordRequirements.atLeastOneUppercase')}</span>
      </div>
      <div className={styles.requirement}>
        <AppIcon icon='questionMark' width={20} height={20} />
        <span>{t('settings.passwordRequirements.atLeastOneLowercase')}</span>
      </div>
      <div className={styles.requirement}>
        <AppIcon icon='questionMark' width={20} height={20} />
        <span>{t('settings.passwordRequirements.atLeastOneSpecial')}</span>
      </div>
    </>
  );

  useEffect(() => {
    if (show) {
      setView('root');
    }
  }, [show]);

  const handleClose = useCallback(() => {
    resetFields();
    onClose();
  }, [resetFields, onClose]);

  const onBackdropClick = useCallback(() => {
    handleClose();
  }, [handleClose]);

  const goBack = () => {
    if (view === 'change-password') {
      resetFields();
    }
    setView(prev =>
      prev === 'interface-language' ? 'language' : prev === 'change-password' ? 'security' : 'root'
    );
  };

  const openLanguage = () => setView('language');
  const openInterfaceLanguage = () => {
    if (!canOpenInterfaceLanguage) return;
    setView('interface-language');
  };

  useEffect(() => {
    if (view === 'interface-language') {
      const current = (user?.metadata?.languagePreferences?.interfaceLanguage as string) || 'en';
      setSelectedInterfaceLang(current);
    }
  }, [view, user?.metadata?.languagePreferences?.interfaceLanguage]);

  return (
    <AppModal
      show={show}
      type='slide-horizontal-right'
      modalContentClassName={styles.container}
      onBackdropClick={onBackdropClick}
      blockBodyScroll={true}
    >
      <button className={styles.closeBtn} onClick={handleClose} aria-label='Close settings'>
        <AppIcon icon='close' />
      </button>
      <div className={styles.settingsModal}>
        <div className={styles.header}>
          {view !== 'root' && !(view === 'interface-language' && !canOpenInterfaceLanguage) && (
            <button className={styles.backBtn} onClick={goBack} aria-label={t('back').toString()}>
              <AppIcon icon='leftArrow' />
              <span>{t('back')}</span>
            </button>
          )}
          <div className={styles.title}>
            {view === 'root'
              ? t('settings.title')
              : view === 'language'
                ? t('settings.language')
                : view === 'interface-language'
                  ? t('settings.interface_language')
                  : view === 'about-me'
                    ? t('settings.about_me.title')
                    : view === 'security'
                      ? t('settings.security')
                      : ''}
          </div>
        </div>

        <div className={styles.sections}>
          {view === 'root' && (
            <>
              <div className={styles.sectionTitle}>{t('settings.account_settings')}</div>
              <div className={styles.section}>
                <button className={styles.item} onClick={() => setView('about-me')}>
                  <div className={styles.itemLeft}>
                    <AppIcon icon='settingUser' width={24} height={24} />
                    <span className={styles.itemTitle}>{t('settings.about_me.title')}</span>
                  </div>
                  <AppIcon icon='leftArrow' className={styles.arrowIcon} />
                </button>
                <button className={styles.item} onClick={() => setView('security')}>
                  <div className={styles.itemLeft}>
                    <AppIcon icon='lock' width={24} height={24} />
                    <span className={styles.itemTitle}>{t('settings.security')}</span>
                  </div>
                  <AppIcon icon='leftArrow' className={styles.arrowIcon} />
                </button>
              </div>

              <div className={styles.sectionTitle}>{t('settings.preferences')}</div>
              <div className={styles.section}>
                <button className={styles.item} onClick={openLanguage}>
                  <div className={styles.itemLeft}>
                    <AppIcon icon='world2' width={24} height={24} />
                    <span className={styles.itemTitle}>{t('settings.language')}</span>
                  </div>
                  <AppIcon icon='leftArrow' className={styles.arrowIcon} />
                </button>
              </div>
            </>
          )}
          {view === 'language' && (
            <>
              <div className={styles.sectionTitle}>{t('settings.language_of_study')}</div>
              <div className={styles.section}>
                <button className={styles.item} disabled>
                  <div className={styles.itemLeft}>
                    <AppIcon icon={'en'} width={24} height={24} />
                    <span className={styles.itemTitle}>
                      {t(`languages.${user?.metadata.languagePreferences.learningLanguage}`)}
                    </span>
                  </div>
                </button>
              </div>

              <div className={styles.sectionTitle}>{t('settings.interface_language')}</div>
              <div className={styles.section}>
                <button
                  className={styles.item}
                  onClick={openInterfaceLanguage}
                  disabled={!canOpenInterfaceLanguage}
                >
                  <div className={styles.itemLeft}>
                    <AppIcon icon={interfaceLanguageIconKey} width={24} height={24} />
                    <span className={styles.itemTitle}>
                      {t(
                        `languages.${selectedInterfaceLang || user?.metadata.languagePreferences.interfaceLanguage}`
                      )}
                    </span>
                  </div>
                  {canOpenInterfaceLanguage && (
                    <AppIcon icon='leftArrow' className={styles.arrowIcon} />
                  )}
                </button>
              </div>
            </>
          )}

          {view === 'interface-language' && (
            <>
              <div className={styles.sectionTitle}>{t('settings.suggested_languages')}</div>
              <div className={styles.section}>
                <button
                  className={styles.item}
                  onClick={async () => {
                    if (!user?.id) return;
                    const code = user?.metadata?.languagePreferences?.nativeLanguage;
                    setSelectedInterfaceLang(code);
                    try {
                      await userService.setUserLanguageSettings(user, code);
                      await dispatch(userThunks.userInfo({ retryCount: 1 }));
                    } finally {
                      onClose();
                    }
                  }}
                >
                  <div className={styles.itemLeft}>
                    <AppIcon icon={nativeLanguageIconKey} width={24} height={24} />
                    <span className={styles.itemTitle}>
                      {t(`languages.${user?.metadata.languagePreferences.nativeLanguage}`)}
                    </span>
                  </div>
                  {selectedInterfaceLang ===
                    (user?.metadata?.languagePreferences?.nativeLanguage as string) && (
                    <AppIcon icon='VIcon' width={20} height={20} />
                  )}
                </button>

                {user?.metadata?.languagePreferences?.learningLanguage !==
                  user?.metadata?.languagePreferences?.nativeLanguage && (
                  <button
                    className={styles.item}
                    onClick={async () => {
                      if (!user?.id) return;
                      const code =
                        (user?.metadata?.languagePreferences?.learningLanguage as string) || 'en';
                      setSelectedInterfaceLang(code);
                      try {
                        await userService.setUserLanguageSettings(user, code);
                        await dispatch(userThunks.userInfo({ retryCount: 1 }));
                      } finally {
                        onClose();
                      }
                    }}
                  >
                    <div className={styles.itemLeft}>
                      <AppIcon icon={learningLanguageIconKey} width={24} height={24} />
                      <span className={styles.itemTitle}>
                        {t(`languages.${user?.metadata.languagePreferences.learningLanguage}`)}
                      </span>
                    </div>
                    {selectedInterfaceLang ===
                      ((user?.metadata?.languagePreferences?.learningLanguage as string) ||
                        'en') && <AppIcon icon='VIcon' width={20} height={20} />}
                  </button>
                )}
              </div>
            </>
          )}

          {view === 'security' && (
            <>
              <div className={styles.sectionTitle}>{t('changePassword.password')}</div>
              <div className={styles.section}>
                <button className={styles.item} onClick={() => setView('change-password')}>
                  <div className={styles.itemLeft}>
                    <span className={styles.itemTitle}>{t('changePassword.changePassword')}</span>
                  </div>
                  <AppIcon icon='leftArrow' className={styles.arrowIcon} />
                </button>
              </div>
            </>
          )}

          {view === 'change-password' && (
            <>
              <form
                onSubmit={async e => {
                  e.preventDefault();
                  const valid = passwordChange.validateOnly();
                  if (valid) {
                    onClose();
                    passwordChange.onChangePasswordSubmit();
                  }
                }}
              >
                <div className={styles.sectionTitle}>{t('changePassword.currentPassword')}</div>
                <div className={styles.plainSection}>
                  <div className={styles.aboutList}>
                    <div className={styles.inputWrapper}>
                      <Input
                        className={styles.textInput}
                        type='password'
                        name={
                          passwordChange.changePasswordFields.find(
                            (f: any) => f.id === 'current_password'
                          )?.name
                        }
                        value={
                          passwordChange.changePasswordFields.find(
                            (f: any) => f.id === 'current_password'
                          )?.value ?? ''
                        }
                        onChange={
                          passwordChange.changePasswordFields.find(
                            (f: any) => f.id === 'current_password'
                          )?.emitChange as any
                        }
                        autoComplete='off'
                        autoCapitalize='none'
                        autoCorrect='off'
                        spellCheck={false}
                        placeholder={t('changePassword.currentPassword') || ''}
                        error={
                          passwordChange.changePasswordFields.find(f => f.id === 'current_password')
                            ?.error
                        }
                      />
                    </div>
                  </div>
                </div>

                <div className={styles.sectionTitle}>{t('changePassword.newPassword')}</div>
                <div
                  className={((): string => {
                    return showRequirementsError ? styles.plainSection : styles.section;
                  })()}
                >
                  <div className={styles.aboutList}>
                    <div className={styles.inputWrapper}>
                      {(() => {
                        const showRequirementsError =
                          Boolean(npField?.error) &&
                          !!newPwdVal &&
                          !requirementsRegex.test(newPwdVal);
                        return (
                          <Input
                            className={styles.textInput}
                            type='password'
                            name={npField?.name}
                            value={newPwdVal}
                            onChange={npField?.emitChange as any}
                            autoComplete='new-password'
                            autoCapitalize='none'
                            autoCorrect='off'
                            spellCheck={false}
                            placeholder={t('changePassword.newPassword') || ''}
                            error={
                              showRequirementsError ? (
                                <div className={styles.passwordListError}>
                                  {passwordRequirementsList}
                                </div>
                              ) : (
                                (npField?.error as any)
                              )
                            }
                          />
                        );
                      })()}
                      {!passwordChange.hasNewPasswordValue && (
                        <span
                          className={styles.helpIcon}
                          onClick={passwordChange.onTogglePasswordInfo}
                        >
                          <AppIcon icon='helpBtn' width={20} height={20} />
                        </span>
                      )}
                      {passwordChange.showPasswordInfo && !passwordChange.hasNewPasswordValue && (
                        <div className={styles.passwordPopover}>
                          <div className={styles.passwordTitle}>
                            {t('changePassword.requirements')}
                          </div>
                          <div className={styles.passwordList}>{passwordRequirementsList}</div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className={styles.sectionTitle}>{t('changePassword.reEnterNewPassword')}</div>
                <div className={styles.plainSection}>
                  <div className={styles.aboutList}>
                    <div className={styles.inputWrapper}>
                      <Input
                        className={styles.textInput}
                        type='password'
                        name={
                          passwordChange.changePasswordFields.find(
                            (f: any) => f.id === 'new_password_verification'
                          )?.name
                        }
                        value={
                          passwordChange.changePasswordFields.find(
                            (f: any) => f.id === 'new_password_verification'
                          )?.value ?? ''
                        }
                        onChange={
                          passwordChange.changePasswordFields.find(
                            (f: any) => f.id === 'new_password_verification'
                          )?.emitChange as any
                        }
                        autoComplete='new-password'
                        autoCapitalize='none'
                        autoCorrect='off'
                        spellCheck={false}
                        placeholder={t('changePassword.confirmPassword') || ''}
                        error={
                          passwordChange.changePasswordFields.find(
                            (f: any) => f.id === 'new_password_verification'
                          )?.error
                        }
                      />
                    </div>
                  </div>
                </div>
                <div className={styles.buttonWrapper}>
                  <Button
                    type='submit'
                    className={styles.saveBtn}
                    disabled={passwordChange.isLoading}
                  >
                    {t('save')}
                  </Button>
                </div>
              </form>
            </>
          )}

          {view === 'about-me' && (
            <>
              <div className={styles.sectionTitle}>{t('settings.profile')}</div>
              <div className={styles.section}>
                <div className={styles.aboutList}>
                  <div className={styles.aboutItem}>
                    <div className={styles.aboutLeft}>{t('settings.about_me.my_name')}</div>
                    <div className={styles.aboutRight}>
                      {`${user?.metadata?.firstName ?? ''} ${user?.metadata?.lastName ?? ''}`.trim()}
                    </div>
                  </div>
                  <div className={styles.aboutItem}>
                    <div className={styles.aboutLeft}>{t('settings.about_me.my_account')}</div>
                    {/* TODO: change the value to user account name */}
                    <div className={styles.aboutRight}>
                      {user?.metadata?.organizationDetails?.organizationName ?? ''}
                    </div>
                  </div>
                  <div className={styles.aboutItem}>
                    <div className={styles.aboutLeft}>{t('settings.about_me.my_language')}</div>
                    <div className={styles.aboutRight}>
                      {t(`languages.${user?.metadata?.languagePreferences?.nativeLanguage}`)}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </AppModal>
  );
};

export default SettingsModal;
